import L from 'leaflet';

// Fix Leaflet default icon path issue with bundlers
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

// Tile layer configurations per language
// Note: Styles differ slightly - FR tiles are more detailed but show French labels
const TILE_CONFIGS = {
  en: {
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>'
  },
  fr: {
    url: 'https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="https://www.openstreetmap.fr/">OSM France</a>'
  }
};

export const WORLD_BOUNDS = [
  [-60, -180],
  [85, 180]
];

export const DEFAULT_CENTER = [20, 0];
export const DEFAULT_ZOOM = 2;
export const MAX_ZOOM = 10;

export function createTileLayer(lang = 'en') {
  const config = TILE_CONFIGS[lang] || TILE_CONFIGS.en;
  return L.tileLayer(config.url, {
    attribution: config.attribution,
    maxZoom: 19
  });
}

export function calculateDistance(latlng1, latlng2) {
  // Returns distance in meters
  const point1 = L.latLng(latlng1);
  const point2 = L.latLng(latlng2);
  return point1.distanceTo(point2);
}

export function easeInOutQuad(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/**
 * Ray-casting algorithm to check if a point is inside a polygon
 * @param {[number, number]} point - [lng, lat] coordinates
 * @param {Array} ring - Array of [lng, lat] coordinates forming a polygon ring
 * @returns {boolean}
 */
function pointInRing(point, ring) {
  const x = point[0];
  const y = point[1];
  let inside = false;

  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];

    const intersect = ((yi > y) !== (yj > y)) &&
      (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

    if (intersect) inside = !inside;
  }

  return inside;
}

/**
 * Check if a point is inside a GeoJSON geometry (Polygon or MultiPolygon)
 * @param {[number, number]} point - [lng, lat] coordinates
 * @param {Object} geometry - GeoJSON geometry object
 * @returns {boolean}
 */
function pointInGeometry(point, geometry) {
  if (geometry.type === 'Polygon') {
    // Check outer ring (first ring), ignore holes for simplicity
    return pointInRing(point, geometry.coordinates[0]);
  } else if (geometry.type === 'MultiPolygon') {
    // Check each polygon in the MultiPolygon
    for (const polygon of geometry.coordinates) {
      if (pointInRing(point, polygon[0])) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Calculate approximate area of a polygon using the shoelace formula
 * @param {Array} ring - Array of [lng, lat] coordinates
 * @returns {number} Approximate area
 */
function calculatePolygonArea(ring) {
  let area = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    area += (ring[i][0] * ring[i + 1][1]) - (ring[i + 1][0] * ring[i][1]);
  }
  return Math.abs(area / 2);
}

/**
 * Calculate total area of a GeoJSON geometry
 * @param {Object} geometry - GeoJSON geometry object
 * @returns {number} Total area
 */
function calculateGeometryArea(geometry) {
  if (geometry.type === 'Polygon') {
    return calculatePolygonArea(geometry.coordinates[0]);
  } else if (geometry.type === 'MultiPolygon') {
    let totalArea = 0;
    for (const polygon of geometry.coordinates) {
      totalArea += calculatePolygonArea(polygon[0]);
    }
    return totalArea;
  }
  return 0;
}

/**
 * Find all GeoJSON layers that contain the given point
 * Returns smallest area first (to handle enclaves like Lesotho within South Africa)
 * @param {L.LatLng} latlng - Leaflet LatLng object
 * @param {L.GeoJSON} geoJsonLayer - Leaflet GeoJSON layer
 * @returns {Array} Array of layers containing the point, sorted by area (smallest first)
 */
export function findLayersAtPoint(latlng, geoJsonLayer) {
  const point = [latlng.lng, latlng.lat];
  const results = [];

  geoJsonLayer.eachLayer((layer) => {
    // Quick bounds check first (optimization)
    if (layer.getBounds && layer.getBounds().contains(latlng)) {
      // Precise polygon check
      if (layer.feature && layer.feature.geometry) {
        if (pointInGeometry(point, layer.feature.geometry)) {
          results.push(layer);
        }
      }
    }
  });

  // Sort by area (smallest first) to prioritize enclaves
  results.sort((a, b) => {
    const areaA = calculateGeometryArea(a.feature.geometry);
    const areaB = calculateGeometryArea(b.feature.geometry);
    return areaA - areaB;
  });

  return results;
}
